import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { formsApi, getErrorMessage } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, ClipboardEdit, Send, Copy, Trash2, FileSpreadsheet, ExternalLink, CheckCircle2,
} from 'lucide-react';
import { format } from 'date-fns';
import { SendFormDialog } from '../components/forms/SendFormDialog';

const ENTITY_LABELS = {
  candidate: 'Candidate',
  contact: 'Contact',
  placement: 'Placement',
  company: 'Company',
};

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  published: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  archived: 'bg-rose-100 text-rose-700 border-rose-200',
};

export default function FormsPage() {
  const { tenant, getTenantPath } = useAuth();
  const navigate = useNavigate();
  const featureEnabled = !!tenant?.settings?.enable_online_forms;

  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', description: '', entity_type: 'candidate' });

  const [sendDialogFor, setSendDialogFor] = useState(null);

  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await formsApi.list();
      setForms(data || []);
    } catch (err) {
      if (err.response?.status === 403) {
        // Feature off — just show empty state.
        setForms([]);
      } else {
        toast.error(getErrorMessage(err, 'Failed to load forms'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (featureEnabled) loadForms();
    else setLoading(false);
  }, [featureEnabled, loadForms]);

  const handleCreate = async () => {
    if (!newForm.name.trim()) {
      toast.error('Form name is required');
      return;
    }
    setCreating(true);
    try {
      const { data } = await formsApi.create({
        name: newForm.name.trim(),
        description: newForm.description.trim(),
        entity_type: newForm.entity_type,
        fields: [],
        pages: [],
      });
      toast.success('Form created');
      setCreateOpen(false);
      setNewForm({ name: '', description: '', entity_type: 'candidate' });
      navigate(getTenantPath(`/forms/${data.id}`));
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create form'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (form) => {
    if (!window.confirm(`Archive form "${form.name}"? Submissions will be preserved.`)) return;
    try {
      await formsApi.delete(form.id);
      toast.success('Form archived');
      loadForms();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to archive'));
    }
  };

  if (!featureEnabled) {
    return (
      <DashboardLayout>
        <div className="p-8 max-w-3xl mx-auto" data-testid="forms-disabled-state">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
            <FileSpreadsheet className="w-12 h-12 mx-auto text-amber-600 mb-3" />
            <h2 className="font-heading text-2xl text-foreground mb-2">Online Forms</h2>
            <p className="text-base text-muted-foreground mb-4">
              Send Jotform-style online forms to candidates. Responses sync back to Bullhorn.
            </p>
            <p className="text-sm text-amber-800 mb-6">
              This feature is currently <strong>disabled</strong> for your organization.
              An admin can enable it from <strong>Settings → Features</strong>.
            </p>
            <Button onClick={() => navigate(getTenantPath('/settings'))} data-testid="forms-go-to-settings-btn">
              Go to Settings
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8 max-w-7xl mx-auto" data-testid="forms-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl text-foreground tracking-tight">Online Forms</h1>
            <p className="text-base text-muted-foreground mt-1">
              Jotform-style multi-page forms with Bullhorn write-back.
            </p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            data-testid="forms-create-btn"
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            New Form
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-16 text-muted-foreground">Loading…</div>
        ) : forms.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-white p-16 text-center" data-testid="forms-empty-state">
            <ClipboardEdit className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <h3 className="font-heading text-xl text-foreground mb-1">No forms yet</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Build your first online form to start collecting candidate data.
            </p>
            <Button onClick={() => setCreateOpen(true)} className="gap-2" data-testid="forms-create-empty-btn">
              <Plus className="w-4 h-4" />
              Create Form
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {forms.map((form) => (
              <FormCard
                key={form.id}
                form={form}
                onEdit={() => navigate(getTenantPath(`/forms/${form.id}`))}
                onSend={() => setSendDialogFor(form)}
                onSubmissions={() => navigate(getTenantPath(`/forms/${form.id}/submissions`))}
                onDelete={() => handleDelete(form)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="forms-create-dialog">
          <DialogHeader>
            <DialogTitle>New Online Form</DialogTitle>
            <DialogDescription>
              Forms are isolated from PDF templates. Add fields after creating.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="form-name">Form Name</Label>
              <Input
                id="form-name"
                data-testid="forms-create-name-input"
                value={newForm.name}
                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                placeholder="Right to Work form"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="form-desc">Description (optional)</Label>
              <Textarea
                id="form-desc"
                data-testid="forms-create-desc-input"
                value={newForm.description}
                onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <Label>Target Bullhorn Entity</Label>
              <Select
                value={newForm.entity_type}
                onValueChange={(v) => setNewForm({ ...newForm, entity_type: v })}
              >
                <SelectTrigger data-testid="forms-create-entity-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="candidate">Candidate</SelectItem>
                  <SelectItem value="contact">Contact</SelectItem>
                  <SelectItem value="placement">Placement</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="forms-create-cancel-btn">Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="forms-create-confirm-btn">
              {creating ? 'Creating…' : 'Create & Edit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Dialog */}
      {sendDialogFor && (
        <SendFormDialog
          form={sendDialogFor}
          open={!!sendDialogFor}
          onClose={() => setSendDialogFor(null)}
        />
      )}
    </DashboardLayout>
  );
}

function FormCard({ form, onEdit, onSend, onSubmissions, onDelete }) {
  const statusClass = STATUS_COLORS[form.status] || STATUS_COLORS.draft;
  return (
    <div
      className="rounded-2xl border border-border bg-white p-5 hover:shadow-md hover:border-primary/30 transition-all flex flex-col"
      data-testid={`form-card-${form.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg text-foreground truncate" title={form.name}>{form.name}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {ENTITY_LABELS[form.entity_type] || form.entity_type} · {form.fields?.length || 0} fields
          </p>
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${statusClass}`}>
          {form.status}
        </span>
      </div>
      {form.description && (
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{form.description}</p>
      )}
      <div className="flex items-center gap-1 mt-auto pt-3 border-t border-border">
        <Button size="sm" variant="ghost" className="gap-1.5 flex-1" onClick={onEdit} data-testid={`form-edit-btn-${form.id}`}>
          <ClipboardEdit className="w-3.5 h-3.5" /> Edit
        </Button>
        <Button size="sm" variant="ghost" className="gap-1.5 flex-1" onClick={onSubmissions} data-testid={`form-submissions-btn-${form.id}`}>
          <CheckCircle2 className="w-3.5 h-3.5" /> Submissions
        </Button>
        <Button size="sm" variant="default" className="gap-1.5 flex-1" onClick={onSend} data-testid={`form-send-btn-${form.id}`}>
          <Send className="w-3.5 h-3.5" /> Send
        </Button>
        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={onDelete} data-testid={`form-delete-btn-${form.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
